/**
 * Payers that aren't in the Stedi directory but are still valid selections
 * in the eligibility form — RPA-routed vendors like VSP and EyeMed, where
 * the check runs through a portal scraper lane instead of a 270/271 EDI call.
 *
 * Driven by `shared/rpaRegistry.ts` so adding a new RPA vendor there
 * automatically surfaces it in PayerSearch. Each entry's
 * `tradingPartnerServiceId` is a sentinel — the backend router picks it
 * up by equality before any Stedi call is attempted.
 *
 * Adding a new vendor's logo:
 *   1. Drop the asset into `src/assets/`
 *   2. Extend `VENDOR_LOGOS` below, keyed by `RpaVendorId`
 */

import { RPA_VENDORS, type RpaVendorId } from '../../shared/rpaRegistry'
import type { PayerOption } from '../components/PayerSearch'
import vspLogo from '../assets/vsp-logo.png'
import eyemedLogo from '../assets/eyemed-logo.svg'

const VENDOR_LOGOS: Partial<Record<RpaVendorId, string>> = {
  vsp: vspLogo,
  eyemed: eyemedLogo,
}

// One option per search entry where the vendor has several names (Versant:
// Davis Vision, Superior Vision), else the vendor's own.
export const HARDCODED_PAYERS: ReadonlyArray<PayerOption> = RPA_VENDORS.flatMap(v =>
  (v.searchEntries ?? [{ name: v.displayName, tpIdSentinel: v.tpIdSentinel }]).map(entry => ({
    name: entry.name,
    tradingPartnerServiceId: entry.tpIdSentinel,
    // RPA vendors don't have a Stedi ID — they never hit Stedi's API. The
    // RPA sentinel in tradingPartnerServiceId is what the router keys off.
    stediId: null,
    logoUrl: VENDOR_LOGOS[v.id] ?? null,
  })),
)

/**
 * Return hardcoded payers whose name matches the query (case-insensitive
 * substring). Empty/blank query returns an empty list — hardcoded entries
 * should only surface when the user actively searches for them.
 */
export function filterHardcodedPayers(query: string): PayerOption[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return HARDCODED_PAYERS.filter(p => p.name.toLowerCase().includes(q))
}
